import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  selectCurrentMembershipSku,
  selectMembershipSkuLoading,
  updateMembershipSku,
} from "../../slice/membershipSkuSlice";
import type { UpdateMembershipSkuFormData } from "../../schemas/membershipSkuSchema";
import { MembershipSkuForm } from "../MembershipSkuForm/MembershipSkuForm";
import { useToast } from "@/components/ui/use-toast";

interface EditMembershipSkuDrawerProps {
  open: boolean;
  onClose: () => void;
  currentTenant?: string;
}

export const EditMembershipSkuDrawer = ({
  open,
  onClose,
  currentTenant,
}: EditMembershipSkuDrawerProps) => {
  const dispatch = useAppDispatch();
  const currentSku = useAppSelector(selectCurrentMembershipSku);
  const isSubmitting = useAppSelector(selectMembershipSkuLoading);
  const { toast } = useToast();

  const handleSubmit = async (data: UpdateMembershipSkuFormData) => {
    if (!currentSku) return;

    try {
      await dispatch(
        updateMembershipSku({ uuid: currentSku.uuid, data }),
      ).unwrap();
      toast({
        title: "Success",
        description: "Membership SKU pattern updated successfully",
      });
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update membership SKU",
        variant: "destructive",
      });
    }
  };

  if (!currentSku) return null;

  return (
    <Sheet onOpenChange={onClose} open={open}>
      <SheetContent className="sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle>Edit Membership SKU Pattern</SheetTitle>
          <SheetDescription>
            Update the SKU pattern for {currentSku.tenant}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">
          <MembershipSkuForm
            currentTenant={currentTenant}
            defaultValues={{
              skuPattern: currentSku.skuPattern,
              membershipDuration: currentSku.membershipDuration,
              description: currentSku.description,
              isActive: currentSku.isActive,
            }}
            isSubmitting={isSubmitting}
            mode="edit"
            onCancel={onClose}
            onSubmit={handleSubmit}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};
